import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";   
import { opportunitiesService } from "../services/opportunitiesService"; 
import { OpportunitiesResponse } from "../models/response/opportunitiesResponse";
import { OpportunitiesRequest } from "../models/request/opportunitiesRequest";
import { OpportunitiesModel } from "../models/models/opportunitiesModel";
import { OpportunitiesResource } from "../models/resource/opportunitiesResource";
import { OpportunitiesState } from "../models/state/opportunitiesState";
import { FileResource } from "../models/resource/fileResource";

const initialState: OpportunitiesState = {
    opportunitiesList: {
        token: null,
        status: "idle",
        isLoading: false,
        error: null,
        message: "",
        blobData: null,
        data: null,
    },
    file: {
        token: null,
        status: "idle",
        isLoading: false,
        error: null,
        message: "",
        blobData: null,
        data: null,
    },
    opportunities: {
        token: null,
        status: "idle",
        isLoading: false,
        error: null,
        message: "",
        blobData: null,
        data: null,
    },
    opportunity: {
        token: null,
        status: "idle",
        isLoading: false,
        error: null,
        message: "",
        blobData: null,
        data: null,
  },
  opportunityDetails: {
        token: null,
        status: "idle",
        isLoading: false,
        error: null,
        message: "",
        blobData: null,
        data: null,
  },
}; 

export const getOpportunitiesList = createAsyncThunk<
  OpportunitiesResponse,
  { data: OpportunitiesRequest },
  { rejectValue: string }
>(
  "opportunity/getOpportunitiesList",
  async ({ data }, { rejectWithValue }) => {
    try {
      const response =
        await opportunitiesService.getOpportunitiesList(data);
      return response;
    } catch (error: any) {
      return rejectWithValue("An unexpected error occurred");
    }
  }
); 

export const getAllOpportunities = createAsyncThunk<
  OpportunitiesModel,
  void,
  { rejectValue: string }
>(
  "opportunity/getAllOpportunities",
  async (_, { rejectWithValue }) => {
    try {
      const response = await opportunitiesService.getAllOpportunities();
      return response;
    } catch (error: any) {
      return rejectWithValue("An unexpected error occurred");
    }
  }
);

export const getOpportunityById = createAsyncThunk<
  OpportunitiesModel,
  { id: number },
  { rejectValue: string }
>(
  "opportunity/getOpportunityById",
  async ({ id }, { rejectWithValue }) => {
    try {
      const response =
        await opportunitiesService.getOpportunityById(id);
      return response;
    } catch (error: any) {
      return rejectWithValue("An unexpected error occurred");
    }
  }
); 

export const exportOpportunity = createAsyncThunk<
Blob,
  { id: number },
  { rejectValue: string }
>(
  "opportunity/exportOpportunity",
  async ({ id }, { rejectWithValue }) => {
    try {
      const response =
        await opportunitiesService.exportOpportunity(id);
      return response;
    } catch (error: any) {
      return rejectWithValue("An unexpected error occurred");
    }
  }
); 

export const deleteOpportunity = createAsyncThunk<
  OpportunitiesResponse,
  { data: number },
  { rejectValue: string }
>(
  "opportunity/deleteOpportunity",
  async ({ data }, { rejectWithValue }) => {
    try {
      const response =
        await opportunitiesService.deleteOpportunity(data);
      return response;
    } catch (error: any) {
      return rejectWithValue("An unexpected error occurred");
    }
  }
);  

export const saveOpportunity = createAsyncThunk<
OpportunitiesModel,
  { data: OpportunitiesResource },
  { rejectValue: string }
>(
  "opportunity/saveOpportunity",
  async ({ data }, { rejectWithValue }) => {
    try {
      const response = await opportunitiesService.saveOpportunity(data);
      return response;
    } catch (error: any) {
      return rejectWithValue("An unexpected error occurred");
    }
  }
); 

export const getFile = createAsyncThunk<
FileResource,
  { fileUrl: string },
  { rejectValue: string }
>(
  "opportunity/getFile",
  async ({ fileUrl }, { rejectWithValue }) => {
    try {
      const response = await opportunitiesService.getFile(fileUrl);
      return response;
    } catch (error: any) {
      return rejectWithValue("An unexpected error occurred");
    }
  }
); 

const opportunitiesReducer = createSlice({
  name: "opportunity",
  initialState,
  reducers: {
    clearBlobData: (state) => {
      state.opportunityDetails.blobData = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getOpportunitiesList.pending, (state) => {
        state.opportunitiesList.status = "loading";
        state.opportunitiesList.isLoading = true;
      })
      .addCase(getOpportunitiesList.fulfilled, (state, action) => {
        state.opportunitiesList.status = "idle";
        state.opportunitiesList.data = action.payload;
        state.opportunitiesList.isLoading = false;
      })
      .addCase(getOpportunitiesList.rejected, (state, action) => {
        state.opportunitiesList.status = "failed";
        state.opportunitiesList.isLoading = false;
        if (action.payload) {
          state.opportunitiesList.error = action.payload;
          state.opportunitiesList.message = action.payload;
        } else {
          state.opportunitiesList.message = "An unexpected error occurred";
        }
      })
      .addCase(getOpportunityById.pending, (state) => {
        state.opportunityDetails.status = "loading";
        state.opportunityDetails.isLoading = true;
      })
      .addCase(getOpportunityById.fulfilled, (state, action) => {
        state.opportunityDetails.status = "idle";
        state.opportunityDetails.data = action.payload;
        state.opportunityDetails.isLoading = false;
      })
      .addCase(getOpportunityById.rejected, (state, action) => {
        state.opportunityDetails.status = "failed";
        state.opportunityDetails.isLoading = false;
        if (action.payload) {
          state.opportunityDetails.error = action.payload;
          state.opportunityDetails.message = action.payload;
        } else {
          state.opportunityDetails.message = "An unexpected error occurred";
        }
      })
      .addCase(exportOpportunity.pending, (state) => {
        state.opportunityDetails.status = "loading";
        state.opportunityDetails.isLoading = true;
      })
      .addCase(exportOpportunity.fulfilled, (state, action) => {
        state.opportunityDetails.status = "idle";
        state.opportunityDetails.blobData = action.payload;
        state.opportunityDetails.isLoading = false;
      })
      .addCase(exportOpportunity.rejected, (state, action) => {
        state.opportunityDetails.status = "failed";
        state.opportunityDetails.isLoading = false;
        if (action.payload) {
          state.opportunityDetails.error = action.payload;
          state.opportunityDetails.message = action.payload;
        } else {
          state.opportunityDetails.message = "An unexpected error occurred";
        }
      })
      .addCase(saveOpportunity.pending, (state) => {
        state.opportunity.status = "loading";
        state.opportunity.isLoading = true;
      })
      .addCase(saveOpportunity.fulfilled, (state, action) => {
        state.opportunity.status = "idle";
        state.opportunity.data = action.payload;
        state.opportunity.isLoading = false;
      })
      .addCase(saveOpportunity.rejected, (state, action) => {
        state.opportunity.status = "failed";
        state.opportunity.isLoading = false;
        if (action.payload) {
          state.opportunity.error = action.payload;
          state.opportunity.message = action.payload;
        } else {
          state.opportunity.message = "An unexpected error occurred";
        }
      })
      .addCase(getFile.pending, (state) => {
        state.file.status = "loading";
        state.file.isLoading = true;
      })
      .addCase(getFile.fulfilled, (state, action) => {
        state.file.status = "idle";
        state.file.data = action.payload;
        state.file.isLoading = false;
      })
      .addCase(getFile.rejected, (state, action) => {
        state.file.status = "failed";
        state.file.isLoading = false;
        if (action.payload) {
          state.file.error = action.payload;
          state.file.message = action.payload;
        } else {
          state.file.message = "An unexpected error occurred";
        }
      })
      .addCase(getAllOpportunities.pending, (state) => {
        state.opportunities.status = "loading";
        state.opportunities.isLoading = true;
      })
      .addCase(getAllOpportunities.fulfilled, (state, action) => {
        state.opportunities.status = "idle";
        state.opportunities.data = action.payload;
        state.opportunities.isLoading = false;
      })
      .addCase(getAllOpportunities.rejected, (state, action) => {
        state.opportunities.status = "failed";
        state.opportunities.isLoading = false;
        if (action.payload) {
          state.opportunities.error = action.payload;
          state.opportunities.message = action.payload;
        } else {
          state.opportunities.message = "An unexpected error occurred";
        }
      })
      .addCase(deleteOpportunity.pending, (state) => {
        state.opportunitiesList.status = "loading";
      })
      .addCase(deleteOpportunity.fulfilled, (state, action) => {
        state.opportunitiesList.status = "idle";
        state.opportunitiesList.data = action.payload;
        state.opportunitiesList.isLoading = false;
      })
      .addCase(deleteOpportunity.rejected, (state, action) => {
        state.opportunitiesList.status = "failed";
        state.opportunitiesList.isLoading = false;
        if (action.payload) {
          state.opportunitiesList.error = action.payload;
          state.opportunitiesList.message = action.payload;
        } else {
          state.opportunitiesList.message = "An unexpected error occurred";
        }
      })
  },
});

export default opportunitiesReducer.reducer;
export const { clearBlobData } = opportunitiesReducer.actions;